// Theme styles definition
export const THEME_STYLES: Record<string, {
    badge: string,
    blob: string,
    hover: string,
    text: string,
    // Add new liquid properties
    liquid: {
        bg: string,
        border: string,
        shadow: string,
        text: string,
        icon: string
    }
}> = {
    blue: {
        badge: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
        blob: 'bg-blue-500/10',
        hover: 'group-hover:bg-blue-500/20',
        text: 'text-blue-500',
        liquid: {
            bg: 'bg-blue-50/50 dark:bg-blue-900/20',
            border: 'border-blue-200 dark:border-blue-700/30',
            shadow: 'shadow-blue-500/10 hover:shadow-blue-500/20',
            text: 'text-blue-900 dark:text-blue-100',
            icon: 'text-blue-500 dark:text-blue-400'
        }
    },
    orange: {
        badge: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300',
        blob: 'bg-orange-500/10',
        hover: 'group-hover:bg-orange-500/20',
        text: 'text-orange-500',
        liquid: {
            bg: 'bg-orange-50/50 dark:bg-orange-900/20',
            border: 'border-orange-200 dark:border-orange-700/30',
            shadow: 'shadow-orange-500/10 hover:shadow-orange-500/20',
            text: 'text-orange-900 dark:text-orange-100',
            icon: 'text-orange-500 dark:text-orange-400'
        }
    },
    yellow: {
        badge: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
        blob: 'bg-yellow-500/10',
        hover: 'group-hover:bg-yellow-500/20',
        text: 'text-yellow-500',
        liquid: {
            bg: 'bg-yellow-50/50 dark:bg-yellow-900/20',
            border: 'border-yellow-200 dark:border-yellow-700/30',
            shadow: 'shadow-yellow-500/10 hover:shadow-yellow-500/20',
            text: 'text-yellow-900 dark:text-yellow-100',
            icon: 'text-yellow-600 dark:text-yellow-400'
        }
    },
    green: {
        badge: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
        blob: 'bg-green-500/10',
        hover: 'group-hover:bg-green-500/20',
        text: 'text-green-500',
        liquid: {
            bg: 'bg-green-50/50 dark:bg-green-900/20',
            border: 'border-green-200 dark:border-green-700/30',
            shadow: 'shadow-green-500/10 hover:shadow-green-500/20',
            text: 'text-green-900 dark:text-green-100',
            icon: 'text-green-500 dark:text-green-400'
        }
    },
    purple: {
        badge: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300',
        blob: 'bg-purple-500/10',
        hover: 'group-hover:bg-purple-500/20',
        text: 'text-purple-500',
        liquid: {
            bg: 'bg-purple-50/50 dark:bg-purple-900/20',
            border: 'border-purple-200 dark:border-purple-700/30',
            shadow: 'shadow-purple-500/10 hover:shadow-purple-500/20',
            text: 'text-purple-900 dark:text-purple-100',
            icon: 'text-purple-500 dark:text-purple-400'
        }
    },
    white: {
        badge: 'bg-slate-100 dark:bg-slate-500/20 text-slate-700 dark:text-slate-300',
        blob: 'bg-slate-500/10',
        hover: 'group-hover:bg-slate-500/20',
        text: 'text-slate-400',
        liquid: {
            bg: 'bg-white dark:bg-slate-800/80',
            border: 'border-slate-200 dark:border-slate-700',
            shadow: 'shadow-slate-200/50 dark:shadow-black/20 hover:shadow-slate-300/50',
            text: 'text-slate-900 dark:text-white',
            icon: 'text-slate-400 dark:text-slate-500'
        }
    }
};

// =============================================================================
// Color Palettes
// =============================================================================

export const PALETTES = {
    /** 
     * Note Themes (mapped to THEME_STYLES) 
     * These are logical names, not hex codes.
     */
    NOTE_THEMES: [
        { value: 'white', label: 'White', hex: '#ffffff' },
        { value: 'beige', label: 'Beige', hex: '#fef3c7' },
        { value: 'yellow', label: 'Yellow', hex: '#fcd34d' },
        { value: 'orange', label: 'Orange', hex: '#fb923c' },
        { value: 'green', label: 'Green', hex: '#4ade80' },
        { value: 'teal', label: 'Teal', hex: '#2dd4bf' },
        { value: 'blue', label: 'Blue', hex: '#60a5fa' },
        { value: 'purple', label: 'Purple', hex: '#c084fc' },
        { value: 'dark', label: 'Dark', hex: '#1e293b' }
    ],

    /** Standard Text Colors */
    TEXT_COLORS: [
        { value: '#000000', label: 'Black' },
        { value: '#ffffff', label: 'White' },
        { value: '#64748b', label: 'Slate' },
        { value: '#ef4444', label: 'Red' },
        { value: '#f97316', label: 'Orange' },
        { value: '#f59e0b', label: 'Amber' },
        { value: '#22c55e', label: 'Green' },
        { value: '#10b981', label: 'Emerald' },
        { value: '#14b8a6', label: 'Teal' },
        { value: '#06b6d4', label: 'Cyan' },
        { value: '#3b82f6', label: 'Blue' },
        { value: '#6366f1', label: 'Indigo' },
        { value: '#8b5cf6', label: 'Violet' },
        { value: '#a855f7', label: 'Purple' },
        { value: '#d946ef', label: 'Fuchsia' },
        { value: '#ec4899', label: 'Pink' },
        { value: '#f43f5e', label: 'Rose' },
    ],

    /** Shape Fill Colors */
    SHAPE_FILLS: [
        { value: '#3B82F6', label: 'Blue' },
        { value: '#10B981', label: 'Green' },
        { value: '#F59E0B', label: 'Amber' },
        { value: '#EF4444', label: 'Red' },
        { value: '#8B5CF6', label: 'Purple' },
        { value: '#EC4899', label: 'Pink' },
        { value: '#1F2937', label: 'Dark' },
        { value: '#FFFFFF', label: 'White' },
        { value: 'transparent', label: 'None' },
    ],

    /** Shape Stroke Colors */
    SHAPE_STROKES: [
        { value: 'transparent', label: 'None' },
        { value: '#1F2937', label: 'Dark' },
        { value: '#3B82F6', label: 'Blue' },
        { value: '#EF4444', label: 'Red' },
        { value: '#FFFFFF', label: 'White' },
    ],

    /** Drawing / Pen Colors */
    DRAWING_COLORS: [
        '#000000', // Black
        '#FFFFFF', // White
        '#525252', // Neutral Gray
        '#EF4444', // Red 500
        '#F97316', // Orange 500
        '#F59E0B', // Amber 500
        '#EAB308', // Yellow 500
        '#84CC16', // Lime 500
        '#22C55E', // Green 500
        '#10B981', // Emerald 500
        '#06B6D4', // Cyan 500
        '#3B82F6', // Blue 500
        '#6366F1', // Indigo 500
        '#8B5CF6', // Violet 500
        '#D946EF', // Fuchsia 500
        '#EC4899', // Pink 500
    ]
};
